/**
 * @file Stepper minimal raffiné du widget de réservation (passé + courante).
 * @module packages.common.components.table-reservation.components.ReservationBreadcrumb
 *
 * Affiche les étapes franchies + l'étape courante sous forme de pastilles
 * rondes contenant chacune une icône MDI outline, reliées par une ligne fine
 * continue. L'étape courante est distinguée par un fond plein en couleur
 * primaire et son label affiché inline à sa droite ; les étapes passées sont
 * cliquables (retour direct via `onStepClick`) avec tooltip indiquant le
 * libellé + la valeur saisie.
 *
 * Choix de design :
 *
 *  - **Pas de chevrons** entre les pastilles — une ligne continue est plus
 *    moderne et signale visuellement la "progression" sans bruit.
 *  - **Ronds 28px** avec icône centrée à 16px — proportion équilibrée,
 *    suffisamment petite pour rester compacte sur mobile.
 *  - **Couleur primaire saturée pour la courante**, atténuée pour les passées —
 *    hiérarchie immédiate sans surcharger l'œil.
 *  - **Label inline à droite de la courante uniquement** — économie d'espace
 *    sans renoncer à la lisibilité de "où je suis".
 *  - **Tooltip au hover sur les passées** : `Label — valeur saisie`, qui sert
 *    d'aperçu avant de cliquer pour modifier.
 */
import React from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';

// hooks & utils
import {BASE_STEPS} from '../constants/steps';
import {getStepSummary, STEP_ORDER} from '../utils/stepSummary';
import Iconify from 'packages/common/components/iconify';

// ----------------------------------------------------------------------

const NODE_SIZE = 28;
const ICON_SIZE = 16;
const CONNECTOR_HEIGHT = 1;
const ROW_GAP = 0;

const Wrapper = styled(Box)(({theme}) => ({
  padding: theme.spacing(1.25, 2),
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(8px)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: 1000,
}));

const Row = styled(Box)({
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'gap': ROW_GAP,
  'flexWrap': 'nowrap',
  'overflowX': 'auto',
  'scrollbarWidth': 'none',
  'msOverflowStyle': 'none',
  '&::-webkit-scrollbar': {display: 'none'},
});

const Node = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['active', 'primaryColor', 'interactive'].includes(prop),
})(({theme, active, primaryColor, interactive}) => {
  const accent = primaryColor || theme.palette.primary.main;
  const inactiveBg = alpha(accent, 0.1);
  const inactiveColor = alpha(accent, 0.7);

  return {
    'width': NODE_SIZE,
    'height': NODE_SIZE,
    'minWidth': NODE_SIZE,
    'borderRadius': '50%',
    'display': 'inline-flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'backgroundColor': active ? accent : inactiveBg,
    'color': active ? theme.palette.common.white : inactiveColor,
    'boxShadow': active ? `0 2px 6px ${alpha(accent, 0.25)}` : 'none',
    'transition': 'background-color 180ms ease-out, box-shadow 180ms ease-out, color 180ms ease-out, transform 120ms ease-out',
    'cursor': interactive ? 'pointer' : 'default',
    'flexShrink': 0,
    '&:hover': interactive ? {
      backgroundColor: alpha(accent, 0.22),
      color: accent,
      transform: 'scale(1.05)',
    } : {},
    '&:focus-visible': {
      outline: `2px solid ${accent}`,
      outlineOffset: 3,
    },
  };
});

const Connector = styled('span', {
  shouldForwardProp: (prop) => prop !== 'primaryColor',
})(({theme, primaryColor}) => ({
  display: 'inline-block',
  height: CONNECTOR_HEIGHT,
  width: 18,
  backgroundColor: alpha(primaryColor || theme.palette.primary.main, 0.25),
  flexShrink: 0,
}));

const CurrentLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'primaryColor',
})(({theme, primaryColor}) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 6,
  marginLeft: 8,
  color: primaryColor || theme.palette.primary.main,
  whiteSpace: 'nowrap',
}));

// ----------------------------------------------------------------------

const STEP_ICONS = {
  [BASE_STEPS.SERVICE_GUESTS]: 'mdi:silverware-fork-knife',
  [BASE_STEPS.DATE]: 'mdi:calendar-blank-outline',
  [BASE_STEPS.ROOM]: 'mdi:floor-plan',
  [BASE_STEPS.TIME]: 'mdi:clock-outline',
  [BASE_STEPS.FORMULA]: 'mdi:book-open-outline',
  [BASE_STEPS.CONTACT]: 'mdi:account-outline',
  [BASE_STEPS.DETAILS]: 'mdi:note-text-outline',
  [BASE_STEPS.PAYMENT]: 'mdi:credit-card-outline',
};

function getStepLabels(t) {
  return {
    [BASE_STEPS.SERVICE_GUESTS]: t('widgets.reservation.steps.service'),
    [BASE_STEPS.DATE]: t('widgets.reservation.steps.date'),
    [BASE_STEPS.ROOM]: t('widgets.reservation.steps.room'),
    [BASE_STEPS.TIME]: t('widgets.reservation.steps.time'),
    [BASE_STEPS.FORMULA]: t('widgets.reservation.steps.formula'),
    [BASE_STEPS.CONTACT]: t('widgets.reservation.steps.contact'),
    [BASE_STEPS.DETAILS]: t('widgets.reservation.steps.details'),
    [BASE_STEPS.PAYMENT]: t('widgets.reservation.steps.payment'),
  };
}

/**
 * Construit la liste des étapes à afficher : SERVICE_GUESTS (toujours franchi
 * quand le breadcrumb est visible) + les étapes de `availableSteps.steps`
 * filtrées par `maxStepReached`. Ordre canonique préservé via `STEP_ORDER`.
 *
 * @param {Object} availableSteps
 * @param {string} maxStepReached
 * @return {string[]}
 */
function buildVisibleSteps(availableSteps, maxStepReached) {
  const reachedIndex = STEP_ORDER.indexOf(maxStepReached);
  const candidates = [BASE_STEPS.SERVICE_GUESTS, ...availableSteps.steps];
  return candidates.filter((step) => {
    const idx = STEP_ORDER.indexOf(step);
    return idx !== -1 && idx <= reachedIndex;
  });
}

// ----------------------------------------------------------------------

/**
 * Stepper minimal raffiné — pastilles rondes reliées + label inline pour la courante.
 *
 * @param {Object} props
 * @param {string} props.currentStep
 * @param {string} props.maxStepReached
 * @param {Object} props.availableSteps
 * @param {Object} props.formData
 * @param {string} [props.primaryColor]
 * @param {Function} props.onStepClick - `(step) => void` au clic d'une étape passée
 * @param {Function} props.t
 * @return {JSX.Element|null}
 */
export function ReservationBreadcrumb({
  currentStep,
  maxStepReached,
  availableSteps,
  formData,
  primaryColor,
  onStepClick,
  t,
}) {
  const labels = getStepLabels(t);
  const visibleSteps = buildVisibleSteps(availableSteps, maxStepReached);

  // Pas de breadcrumb sur l'accueil ni sur la confirmation.
  if (
    visibleSteps.length === 0 ||
    currentStep === BASE_STEPS.SERVICE_GUESTS ||
    currentStep === BASE_STEPS.CONFIRMATION
  ) {
    return null;
  }

  const handleClick = (step) => {
    if (step === currentStep || typeof onStepClick !== 'function') return;
    onStepClick(step);
  };

  return (
    <Wrapper component="nav" aria-label="Progression de la réservation">
      <Row role="list">
        {visibleSteps.map((step, index) => {
          const isActive = step === currentStep;
          const icon = STEP_ICONS[step];
          const label = labels[step] || step;
          const summary = getStepSummary(step, formData);
          const tooltip = summary ? `${label} — ${summary}` : label;

          return (
            <React.Fragment key={step}>
              {index > 0 && <Connector primaryColor={primaryColor} aria-hidden="true" />}

              {isActive ? (
                <Box sx={{display: 'inline-flex', alignItems: 'center'}}>
                  <Node
                    role="listitem"
                    active
                    primaryColor={primaryColor}
                    interactive={false}
                    disabled
                    aria-current="step"
                  >
                    <Iconify icon={icon} width={ICON_SIZE} aria-hidden="true" />
                  </Node>
                  <CurrentLabel primaryColor={primaryColor}>
                    <Typography
                      component="span"
                      sx={{
                        typography: 'body2',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        letterSpacing: 0.2,
                      }}
                    >
                      {label}
                    </Typography>
                  </CurrentLabel>
                </Box>
              ) : (
                <Tooltip title={tooltip} arrow enterTouchDelay={0} placement="bottom">
                  <Node
                    role="listitem"
                    primaryColor={primaryColor}
                    interactive
                    onClick={() => handleClick(step)}
                    aria-label={`${label}${summary ? `, ${summary}` : ''}. Cliquer pour modifier.`}
                  >
                    <Iconify icon={icon} width={ICON_SIZE} aria-hidden="true" />
                  </Node>
                </Tooltip>
              )}
            </React.Fragment>
          );
        })}
      </Row>
    </Wrapper>
  );
}

ReservationBreadcrumb.propTypes = {
  currentStep: PropTypes.string.isRequired,
  maxStepReached: PropTypes.string.isRequired,
  availableSteps: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  primaryColor: PropTypes.string,
  onStepClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
